import React from 'react';

/**
 * CandidateSearchCardSkeleton - Loading skeleton for candidate cards in the candidate search page
 * 
 * @param {number} delay - Animation delay in milliseconds for staggered loading effect
 * @param {string} viewMode - Display mode: 'grid' or 'list'
 */
const CandidateSearchCardSkeleton = ({ delay = 0, viewMode = 'grid' }) => {
  
  // List view layout skeleton
  if (viewMode === 'list') {
    return (
      <div 
        className="bg-surface rounded-lg border border-border p-4 animate-pulse hover:border-primary hover:shadow-md transition-all duration-200"
        style={{ animationDelay: `${delay}ms` }}
      >
        <div className="flex items-center space-x-4">
          {/* Profile Picture Skeleton */}
          <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center mb-2">
                  {/* Name */}
                  <div className="w-32 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mr-2"></div>
                  {/* Match Score */}
                  <div className="w-20 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                </div>
                {/* Title */}
                <div className="w-48 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
              <div className="text-right flex-shrink-0 ml-4">
                {/* Rate */}
                <div className="w-16 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-1"></div>
                {/* Rating */}
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-12 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
              </div>
            </div>
            
            {/* Skills */}
            <div className="md:flex md:items-center md:justify-between mt-3">
              <div className="flex flex-wrap gap-2 mb-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-16 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                ))}
                <div className="w-12 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
              </div>
              
              <div className="flex flex-wrap items-center space-x-4 mt-3 md:mt-0">
                {/* Location */}
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-20 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
                {/* Experience */}
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-16 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-4">
              {/* Bio */}
              <div className="max-w-sm">
                <div className="w-64 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Budget Compatibility */}
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-24 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                  <div className="w-20 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-24 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Grid view layout skeleton (default)
  return (
    <div 
      className="bg-surface rounded-lg border border-border p-6 animate-pulse hover:border-primary hover:shadow-md transition-all duration-200"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start space-x-4">
        {/* Profile Picture Skeleton */}
        <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center mb-1">
                {/* Name */}
                <div className="w-32 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mr-2"></div>
                {/* Match Score */}
                <div className="w-20 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
              </div>
              {/* Title */}
              <div className="w-48 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
            <div className="text-right flex-shrink-0 ml-4">
              {/* Rate */}
              <div className="w-16 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-1"></div>
              {/* Rating */}
              <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                <div className="w-12 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="mb-3 space-y-1">
            <div className="w-full h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            <div className="w-3/4 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          </div>

          {/* Skills */}
          <div className="flex flex-wrap gap-2 mb-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-16 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
            ))}
            <div className="w-12 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
          </div>

          {/* Grid Info */}
          <div className="grid grid-cols-2 gap-4 text-xs mb-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="w-20 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="w-16 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="w-18 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="w-16 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
          </div>
          
          {/* Budget Compatibility Indicator */}
          <div className="mb-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="w-24 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
            <div className="w-20 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            <div className="w-24 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateSearchCardSkeleton;
